<template>
	<nav
		class="pagination-navigation"
		:aria-label="translate('pagination.ariaLabel')"
	>
		<div :class="['pagination-navigation__container', { 'pagination-navigation__container--centered': centered }]">
			<button
				v-touch-target
				class="pagination-navigation__to-start"
				type="button"
				:disabled="disabledPrev"
				:aria-label="translate('pagination.toStart')"
				@click="$emit('to-start')"
			>
				<FaIcon :icon="['fa-kit', 'fa-chevron-to-start']" />
			</button>
			<NavigationButton
				:direction="'prev'"
				:disabled="disabledPrev"
				class="pagination-navigation__prev"
				:aria-label="translate('pagination.toPrevious')"
				@click="$emit('prev')"
			/>
			<span
				class="pagination-navigation__page-number"
				aria-current="page"
			>
				<slot
					v-if="!hideLabel"
					name="page-number"
					:page="page"
					:total-pages="totalPages"
				>
					{{ calculatePageNumber }}
				</slot>
			</span>
			<NavigationButton
				:direction="'next'"
				:disabled="disabledNext"
				class="pagination-navigation__next"
				:aria-label="translate('pagination.toNext')"
				@click="$emit('next')"
			/>
		</div>
	</nav>
</template>

<script lang="ts" setup>
import NavigationButton from '@lmt-rpb-new/atoms/NavigationButton/NavigationButton.vue';
import { computed } from 'vue';
import { useTranslations } from 'src/storybook/src/composables/translations';
import FaIcon from '@lmt-rpb/Atoms/FaIcon/FaIcon.vue';
import { vTouchTarget } from 'src/directives/TouchTarget/touchTarget';

const { translate } = useTranslations();

const props = withDefaults(
	defineProps<{
		/**
		 * Current page
		 *
		 * @default 1
		 */
		page?: number;
		/**
		 * The total number of pages
		 */
		totalPages?: number;
		/**
		 * Should the next button be enabled if there is next page but not known yet pages count.
		 *
		 * @default true
		 */
		hasNext?: boolean;

		/**
		 * Loading state
		 */
		loading?: boolean;
		/**
		 * Hide the page number label. For example, when we don' t know the total number of pages yet.
		 *
		 * @default false
		 */
		hideLabel?: boolean;
		/**
		 * Centered pagination navigation with toStart button to be absolute.
		 *
		 * @default false
		 */
		centered?: boolean;
	}>(),
	{
		page: 1,
		hasNext: true,
		loading: false,
		hideLabel: false,
		totalPages: undefined,
		centered: false,
	}
);

defineEmits(['prev', 'next', 'to-start']);

const isStartPage = computed(() => props.page === 1);
const isEndPage = computed(() => props.page === props.totalPages);
const disabledNext = computed(() => !props.hasNext || isEndPage.value || props.loading);
const disabledPrev = computed(() => isStartPage.value || props.loading);

defineSlots<{
	['page-number']: {
		page: number;
		totalPages: number;
	};
}>();

const calculatePageNumber = computed(() => {
	if (!props.totalPages) {
		return translate('pagination.pageNumberWithoutTotal', {
			page: props.page,
		});
	}
	return translate('pagination.pageNumber', {
		page: props.page,
		totalPages: props.totalPages,
	});
});
</script>

<style lang="scss" scoped>
.pagination-navigation {
	display: flex;
	justify-content: center;
	align-items: center;
	font-family: $font-family;

	&__to-start {
		appearance: none;
		border: none;
		background: none;
		color: $color-link;
		font-size: $font-small-1;
		padding: 0;
		display: inline-flex;
		align-items: center;
		justify-content: center;

		&:disabled {
			cursor: not-allowed;
			color: $color-search-mask-border;
		}

		&:not(:disabled) {
			@include focus-visible-state(0, $color-link);
		}
	}

	&__container {
		position: relative;
		display: flex;
		align-items: center;
		gap: $spacer-2;

		&--centered {
			.pagination-navigation__to-start {
				position: absolute;
				right: calc(100% + $spacer-4);
				top: 50%;
				transform: translateY(-50%);
			}
		}
	}

	&__page-number {
		color: $color-text;
		font-size: $font-small-2;
		font-weight: $font-weight-regular;
		min-width: 9.6rem;
		text-align: center;
	}
}
</style>
